import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import specializedReportService from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import { requireArrayIn } from '@api/v1/readShape'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField, choiceField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import ReportGroupedGrid from '@components/reports/ReportGroupedGrid'
import { withAliasKeys } from '@components/reports/aliasedGridColumns'
import { useReportExportView } from '@components/reports/reportExportViewContext'
import { fmtDisplayDate } from '@utils/displayDate'
import { calendarUtcWire, prepareCalendarRows } from './calendarRows'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3
const TYPE_MATTERS = 1001

const startOfMonth = () => dayjs().startOf('month').format('YYYY-MM-DD')
const endOfMonth = () => dayjs().endOf('month').format('YYYY-MM-DD')

// Himaad UI feedback, 2026-08-27: the Calendar report's local unpadded
// 'M/D/YYYY' copy is gone — the Date column below comes from `fmtDisplayDate`
// (MM/DD/YYYY, src/utils/displayDate.js), the one Report Center display
// formatter. Legitimate here because this page SYNTHESIZES its own dataCells
// from CB's raw JSON (`toLeaf`), unlike the 119 generic reports whose cells CB
// pre-formats server-side. `startOfMonth`/`endOfMonth` above are WIRE values and
// stay ISO.
//
// Spec 203: the Date column and the Date GROUP HEADER are now the same value. CB's
// dispatcher sends `appointmentDateString` null (only legacy's MVC view model fills
// it), so `prepareCalendarRows` (calendarRows.js) writes it from the event's own day
// — the viewer's local day for a timed event, CB's calendar date for an all-day one —
// and the header reads MM/DD/YYYY like the column beneath it.
//
// `fmtTime` below is a TIME, not a date. cb-api sends CB's dates as ISO since spec
// 203; they were WCF '/Date(…)/' strings that dayjs could not read, so every time was
// blank on the first run that ever succeeded.
const fmtTime = (start, end, allDay) => {
  if (allDay) return 'All day'
  const s = start ? dayjs(start) : null
  const e = end ? dayjs(end) : null
  if (s && s.isValid() && e && e.isValid()) return `${s.format('h:mma')} – ${e.format('h:mma')}`
  if (s && s.isValid()) return s.format('h:mma')
  return ''
}

// displayValue / groupHeaderContent are injected as HTML by the universal
// grid — escape raw values (event titles, matter names, locations).
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const SUB_TYPES = [
  { value: '', label: 'All' },
  { value: '1', label: 'Appointments' },
  { value: '2', label: 'Tasks' },
]

// `userIds` and `subTypes` are `string` on CalendarReportFilter
// (AccountingModels.cs:1297,1309), so both are multi-CAPABLE — but the delimiter
// legacy uses was not confirmed, and an unrecognised one binds and then silently
// matches nothing, which is indistinguishable from "no results". Both therefore
// stay SINGLE-select here, exactly as before. Widening them is tracked on
// CC-50013/CC-50148 and needs a live-payload check first.
//
// `emptyId: ''` on the user lookup is required: the C# field is a string, so
// null would not bind (the baseline wire carries `"userIds":""`).
const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'clientId', nameKey: 'clientName' }),
  objectField({ key: 'matter', label: 'Matter', typeId: TYPE_MATTERS, idKey: 'billingObjectId', nameKey: 'billingObjectName' }),
  objectField({ key: 'user', label: 'User', typeId: TYPE_USERS, idKey: 'userIds', nameKey: 'userNames', emptyId: '' }),
  choiceField({
    key: 'subTypes', label: 'Type', valueKey: 'subTypes',
    options: SUB_TYPES.map((o) => ({ value: o.value, label: o.label })),
  }),
  dateRangeField({
    key: 'activityDates', label: 'Date Range',
    startKey: 'activityDateStart', endKey: 'activityDateEnd', emptyValue: null, omitAll: true,
  }),
]

const makeDefaults = () => ({
  clientId: null, clientName: '',
  userIds: '', userNames: '',
  billingObjectId: null, billingObjectName: '',
  activityDateStart: startOfMonth(),
  activityDateEnd: endOfMonth(),
  subTypes: '',
})

// The wire object. `billableObjectType` has no control. The UTC range and
// `timedifference` (the caller's UTC offset, in HOURS) are computed per run exactly
// as legacy's view computes them — spec 203, calendarRows.js `calendarUtcWire` —
// rather than held in state. CB requires both UTC dates, and the export applies the
// offset to every timed event.
const toWire = (f) => ({
  clientId: f.clientId,
  clientName: f.clientName,
  userIds: f.userIds,
  userNames: f.userNames,
  billingObjectId: f.billingObjectId,
  billingObjectName: f.billingObjectName,
  activityDateStart: f.activityDateStart || null,
  activityDateEnd: f.activityDateEnd || null,
  ...calendarUtcWire(f.activityDateStart, f.activityDateEnd),
  subTypes: f.subTypes,
  billableObjectType: TYPE_MATTERS,
})

// All-text report: no numeric columns → no dataValue, no footers.
// CC-50291: stable hand-written columnIds double as the rename keys.
const CAL_COLUMNS = withAliasKeys([
  { columnId: 'date', label: 'Date' },
  { columnId: 'time', label: 'Time' },
  { columnId: 'title', label: 'Title' },
  { columnId: 'type', label: 'Type' },
  { columnId: 'user', label: 'User' },
  { columnId: 'matter', label: 'Matter' },
  { columnId: 'location', label: 'Location' },
])

const toLeaf = (r) => ({
  isGroup: false,
  dataCells: [
    { columnId: 'date', displayValue: esc(fmtDisplayDate(r.calendarDay)) },
    { columnId: 'time', displayValue: esc(fmtTime(r.appointmentStartTime, r.appointmentEndTime, r.allDayEvent)) },
    { columnId: 'title', displayValue: esc(r.appointmentName) },
    { columnId: 'type', displayValue: esc(r.appointmentType) },
    { columnId: 'user', displayValue: esc(r.scheduledUsers) },
    { columnId: 'matter', displayValue: esc(r.matterName) },
    { columnId: 'location', displayValue: esc(r.appointmentLocation) },
  ],
})

// CC-49594: publish the grid's export view to the surrounding shell so it can
// offer the client-built PDF. The context provider lives inside
// SpecializedReportShell, so this wrapper must render as a child of the shell.
const PublishedGrid = ({ report, storageKey, pageStateRef, initialPageState }) => {
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  return (
    <ReportGroupedGrid
      report={report}
      storageKey={storageKey}
      exportViewRef={exportViewRef}
      pageStateRef={pageStateRef}
      initialPageState={initialPageState}
    />
  )
}

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const CalendarReport = ({ reportId, name = 'Calendar Report', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  const [groupBy, setGroupBy] = useState('appointmentDateString')
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.activityDateStart, filters.activityDateEnd)), [filters])
  // CC-53132: `null` until a read has actually resolved, NOT `[]`. An empty array
  // is an answer — "this month has no events" — and the grid renders it as one
  // ("0 records", "No data for the selected filters"). Before any read lands, or
  // after one that failed, there is no answer to render.
  const [rows, setRows] = useState(null)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const filterPayload = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => {
    if (!filters.activityDateStart || !filters.activityDateEnd) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'Date range is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        // CC-53132: this was `r?.data?.rows || []`. The live run has been guarded
        // since CC-53052 (specializedReportService.calendar.run → requireArrayIn),
        // but the tray is a SECOND door onto the same grid and it had no guard at
        // all — reportJobService.result returns the stored blob unchecked. So an
        // unreadable snapshot (the documented PascalCase-blob and double-gzip
        // shapes) rendered an empty calendar *under a banner attesting to a
        // specific past run*, which is worse than a bare empty grid: it dates the
        // lie. A genuinely empty snapshot still passes through as [].
        const saved = requireArrayIn(r, 'rows', { noun: 'saved calendar result', emptyNoun: 'calendar' })
        // Stamp the provenance banner only once the snapshot itself has been read,
        // so a throw above cannot leave "saved result as of …" over no data.
        return () => { setSavedAsOf(st?.createdAt || null); setRows(prepareCalendarRows(saved)) }
      }
      const result = await specializedReportService.calendar.run(filterPayload, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setRows(prepareCalendarRows(result)) }
    }, { criteria: { filtersSummary } })
  }, [filterPayload, filters.activityDateStart, filters.activityDateEnd, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Client-side Group By (Date / User / Matter / Type) stays in the page; the
  // grouped rows feed the universal grid's { isGroup, groupHeaderContent,
  // dataRows } shape. Expand/collapse comes from ReportGroupedGrid now.
  const grouped = useMemo(() => {
    if (!groupBy || !rows?.length) return null
    const groups = new Map()
    for (const r of rows) {
      const key = r[groupBy] ?? '(none)'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(r)
    }
    return groups
  }, [groupBy, rows])

  const gridReport = useMemo(() => ({
    columns: CAL_COLUMNS.map((c) => ({ ...c, visible: true, columnAlignment: 'LEFT' })),
    reportData: grouped
      ? Array.from(grouped.entries()).map(([key, items]) => ({
          isGroup: true,
          groupHeaderContent: `${esc(key || '(none)')} <span class="text-xs font-normal text-gray-500">(${items.length})</span>`,
          dataRows: items.map(toLeaf),
        }))
      : (rows || []).map(toLeaf),
    reportFooterCells: [], // no numeric columns → no totals
    // `null`, not 0, for an unresolved read: ReportGroupedGrid prints the number
    // whenever recordCount is non-null and its own neutral "Grouped report" when
    // it is null (ReportGroupedGrid.jsx:1130). A 0 there would be a count of
    // events nobody has looked for.
    recordCount: rows ? rows.length : null,
  }), [grouped, rows])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS / toWire).
  // Group By is NOT a filter — it regroups the already-fetched rows client-side
  // and needs no re-run, so it sits with the actions rather than in the bar.
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'calendar',
        endpoint: (f) => ({ method: 'POST', path: '/reports/calendar', body: toWire(f) }),
      }}
      actions={(
        <>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Group By
            <select
              value={groupBy} aria-label="Group by"
              onChange={(e) => setGroupBy(e.target.value)}
              className="select-rc font-normal normal-case"
            >
              <option value="">No grouping</option>
              <option value="appointmentDateString">Date</option>
              <option value="scheduledUsers">User</option>
              <option value="matterName">Matter</option>
              <option value="appointmentType">Type</option>
            </select>
          </label>
          <ReportViewsMenu
            storageKey="report:calendar"
            currentFilters={filters}
            onApply={view.applyView}
            getPageState={view.getPageState}
          />
        </>
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      download={(format, getToken) =>
        specializedReportService.calendar.download(filterPayload, format, getToken, `calendar-report.${format}`)
      }
    >
      {/* CC-53132: no grid at all until a read has resolved. ReportGroupedGrid has
          no unresolved state of its own — it renders "No data for the selected
          filters" for any empty reportData — so mounting it on `rows === null`
          puts a confident empty calendar underneath the shell's own error pane,
          or (on a pre-flight refusal such as a missing date range) underneath a
          message saying the report was never run. Stale rows from an earlier
          successful run DO stay on screen next to a fresh error, exactly as
          react-query keeps its last good data. */}
      {rows && (
        <PublishedGrid key={view.gridKey} report={gridReport} storageKey="report:calendar" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
      )}
    </SpecializedReportShell>
  )
}

export default CalendarReport
